<script>
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton, CopilotMenuBar },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    onOpenMedia: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    finishAudioRecording: {
      type: Function,
      default: () => {},
    },
    deleteAudioRecording: {
      type: Function,
      default: () => {},
    },
    isRecordingPaused: {
      type: Boolean,
      default: false,
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    showCopilotButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
    'toggle-canned-menu',
    'toggle-note-mode',
    'executeCopilotAction',
  ],
  setup(props) {
    const { setSignatureFlagForInbox, fetchSignatureFlagFromUISettings } =
      useUISettings();

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
    };
  },
  data() {
    return {
      ALLOWED_FILE_TYPES,
      showPlusMenu: false,
      showCopilotMenu: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      // O microfone é uma ação básica do composer em canais compatíveis; não
      // pode desaparecer enquanto o estado assíncrono de feature flags carrega.
      return this.showAudioRecorder;
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      // Use default file types for private notes
      if (this.isOnPrivateNote) {
        return this.ALLOWED_FILE_TYPES;
      }

      let channelType = this.channelType || this.inbox?.channel_type;

      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return this.newConversationModalActive
        ? false
        : '.conversation-details-wrap.chat-whats';
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        case 'recording-paused':
          return 'i-ph-play';
        default:
          return 'i-ph-pause';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    showMicAsSend() {
      return (
        this.isSendDisabled &&
        !this.isRecordingAudio &&
        this.showAudioRecorderButton
      );
    },
    showSendButton() {
      if (!this.isSendDisabled) return true;
      if (this.isRecordingAudio) return true;
      return !this.showAudioRecorderButton;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    // Aceleres: filtro de tipos pro picker "Anexar foto e vídeo" — só
    // image/* e video/*. Validação real do canal continua server-side.
    mediaAcceptTypes() {
      return 'image/*,video/*';
    },
    enableTemplateMenuItem() {
      return this.enableWhatsAppTemplates || this.enableContentTemplates;
    },
    showCannedMenuItem() {
      return !this.isEditorDisabled && !this.isNote;
    },
    showNoteToggleItem() {
      return !this.isEditorDisabled;
    },
    plusMenuHasAnyItem() {
      return (
        this.showAttachButton ||
        this.enableTemplateMenuItem ||
        this.showCannedMenuItem ||
        this.showNoteToggleItem
      );
    },
    showJarvisButton() {
      return this.showCopilotButton;
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handlePlusMenuOutside);
    document.removeEventListener('mousedown', this.handleCopilotMenuOutside);
  },
  methods: {
    composerTooltip(content) {
      return { content, distance: 20 };
    },
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    // ===== Aceleres: menu "+" (substitui a régua de ícones inline) =====
    togglePlusMenu() {
      if (this.showPlusMenu) {
        this.closePlusMenu();
      } else {
        this.openPlusMenu();
      }
    },
    openPlusMenu() {
      this.showPlusMenu = true;
      this.$nextTick(() => {
        document.addEventListener('mousedown', this.handlePlusMenuOutside);
      });
    },
    closePlusMenu() {
      this.showPlusMenu = false;
      document.removeEventListener('mousedown', this.handlePlusMenuOutside);
    },
    toggleCopilotMenu() {
      if (this.showCopilotMenu) {
        this.closeCopilotMenu();
      } else {
        this.openCopilotMenu();
      }
    },
    openCopilotMenu() {
      this.showCopilotMenu = true;
      this.closePlusMenu();
      this.$nextTick(() => {
        document.addEventListener('mousedown', this.handleCopilotMenuOutside);
      });
    },
    closeCopilotMenu() {
      this.showCopilotMenu = false;
      document.removeEventListener('mousedown', this.handleCopilotMenuOutside);
    },
    handlePlusMenuOutside(event) {
      const wrap = this.$refs.plusMenuWrap;
      if (wrap && !wrap.contains(event.target)) {
        this.closePlusMenu();
      }
    },
    handleCopilotMenuOutside(event) {
      const wrap = this.$refs.copilotMenuWrap || this.$refs.plusMenuWrap;
      if (wrap && !wrap.contains(event.target)) {
        this.closeCopilotMenu();
      }
    },
    handleCopilotAction(actionKey, data) {
      this.closeCopilotMenu();
      this.$emit('executeCopilotAction', actionKey, data || this.message);
    },
    triggerHiddenUpload(inputId) {
      const el = document.getElementById(inputId);
      if (el) el.click();
    },
    onSelectImageVideo() {
      this.onOpenMedia();
      this.closePlusMenu();
      this.triggerHiddenUpload('conversationAttachmentMedia');
    },
    onSelectDocument() {
      this.closePlusMenu();
      this.triggerHiddenUpload('conversationAttachmentDoc');
    },
    onSelectTemplate() {
      this.closePlusMenu();
      if (this.enableWhatsAppTemplates) {
        this.$emit('selectWhatsappTemplate');
      } else if (this.enableContentTemplates) {
        this.$emit('selectContentTemplate');
      }
    },
    onSelectCanned() {
      this.closePlusMenu();
      this.$emit('toggle-canned-menu');
    },
    onSelectNoteToggle() {
      this.closePlusMenu();
      this.$emit('toggle-note-mode');
    },
  },
};
</script>

<template>
  <div
    class="reply-bottom-panel flex items-center justify-between gap-3 px-3 py-2"
    :class="wrapClass"
  >
    <div class="left-wrap">
      <!-- Aceleres: ORDEM WhatsApp — "+" (mais opções) PRIMEIRO, emoji
           depois. Antes o emoji vinha primeiro; usuário pediu inverter.
           (pedido 2026-07-01) -->

      <!-- Botão "+" que abre menu com anexar foto/vídeo,
           documento, modelo, respostas rápidas e notas. -->
      <div
        v-if="!isEditorDisabled && plusMenuHasAnyItem"
        ref="plusMenuWrap"
        class="acl-plus-wrap"
      >
        <NextButton
          v-tooltip.top-end="composerTooltip('Mais opções')"
          icon="i-ph-plus"
          slate
          :variant="showPlusMenu ? 'solid' : 'faded'"
          color="slate"
          sm
          @click.stop="togglePlusMenu"
        />
        <Transition name="acl-plus-menu-fade">
          <div v-if="showPlusMenu" class="acl-plus-menu" role="menu">
            <button
              v-if="showAttachButton"
              type="button"
              class="acl-plus-menu__item acl-plus-menu__item--violet"
              role="menuitem"
              @click="onSelectImageVideo"
            >
              <span class="acl-plus-menu__icon"><i class="i-ph-image-fill" /></span>
              <span class="acl-plus-menu__label">Anexar foto e vídeo</span>
            </button>
            <button
              v-if="showAttachButton"
              type="button"
              class="acl-plus-menu__item acl-plus-menu__item--blue"
              role="menuitem"
              @click="onSelectDocument"
            >
              <span class="acl-plus-menu__icon"><i class="i-ph-file-text-fill" /></span>
              <span class="acl-plus-menu__label">Documento</span>
            </button>
            <button
              v-if="enableTemplateMenuItem"
              type="button"
              class="acl-plus-menu__item acl-plus-menu__item--teal"
              role="menuitem"
              @click="onSelectTemplate"
            >
              <span class="acl-plus-menu__icon"><i class="i-ph-whatsapp-logo-fill" /></span>
              <span class="acl-plus-menu__label">Modelo de mensagem</span>
            </button>
            <button
              v-if="showCannedMenuItem"
              type="button"
              class="acl-plus-menu__item acl-plus-menu__item--amber"
              role="menuitem"
              @click="onSelectCanned"
            >
              <span class="acl-plus-menu__icon"><i class="i-ph-lightning-fill" /></span>
              <span class="acl-plus-menu__label">Mensagens rápidas</span>
            </button>
            <button
              v-if="showNoteToggleItem"
              type="button"
              class="acl-plus-menu__item acl-plus-menu__item--slate"
              :class="{ 'acl-plus-menu__item--active': isNote }"
              role="menuitem"
              @click="onSelectNoteToggle"
            >
              <span class="acl-plus-menu__icon"><i class="i-ph-note-pencil" /></span>
              <span class="acl-plus-menu__label">
                {{
                  isNote
                    ? $t('CONVERSATION.REPLYBOX.BACK_TO_REPLY')
                    : $t('CONVERSATION.REPLYBOX.INTERNAL_NOTE_MENU')
                }}
              </span>
            </button>
          </div>
        </Transition>
      </div>

      <!-- Aceleres: Copiloto JARVIS entre "+" e emoji. Reusa o mesmo
           CopilotMenuBar do editor para manter as ações existentes. -->
      <div
        v-if="showJarvisButton"
        ref="copilotMenuWrap"
        class="acl-copilot-wrap"
      >
        <button
          v-tooltip.top-end="
            composerTooltip(
              $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.ASK_COPILOT')
            )
          "
          type="button"
          class="acl-copilot-button"
          :class="{ 'is-active': showCopilotMenu }"
          :aria-label="
            $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.ASK_COPILOT')
          "
          @click.stop="toggleCopilotMenu"
        >
          <i class="i-fluent-circle-sparkle-24-regular" aria-hidden="true" />
        </button>
        <Transition name="acl-plus-menu-fade">
          <CopilotMenuBar
            v-if="showCopilotMenu"
            class="acl-copilot-menu"
            :conversation-id="conversationId"
            :editor-content="message"
            @execute-copilot-action="handleCopilotAction"
          />
        </Transition>
      </div>

      <!-- Aceleres: emoji/figurinha DEPOIS do "+" (ordem WhatsApp). -->
      <NextButton
        v-if="!isEditorDisabled"
        v-tooltip.top-end="
          composerTooltip($t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON'))
        "
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        class="acl-composer-emoji"
        type="button"
        aria-haspopup="dialog"
        :aria-expanded="showEmojiPicker"
        @click.stop="toggleEmojiPicker"
      />

      <!-- Aceleres: file-uploads escondidos disparados pelos itens do menu.
           1) Media — só image/* e video/* (filtro do picker)
           2) Documento — allowedFileTypes do canal (full list) -->
      <FileUpload
        v-if="showAttachButton"
        ref="uploadMediaRef"
        input-id="conversationAttachmentMedia"
        :size="4096 * 4096"
        :accept="mediaAcceptTypes"
        :multiple="enableMultipleFileUpload"
        :drop="false"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        class="acl-hidden-uploader"
        @input-file="onFileUpload"
      >
        <span class="sr-only">Anexar foto/vídeo</span>
      </FileUpload>
      <FileUpload
        v-if="showAttachButton"
        input-id="conversationAttachmentDoc"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        class="acl-hidden-uploader"
        @input-file="onFileUpload"
      >
        <span class="sr-only">Anexar documento</span>
      </FileUpload>

      <!-- Aceleres: indicador "Gravando" estilo WhatsApp — ponto vermelho
           pulsante + rótulo. Deixa óbvio que a gravação começou (mesmo se o
           waveform demorar a renderizar) e torna o estado inambíguo. -->
      <span
        v-if="showAudioPlayStopButton && isRecordingAudio && recordingAudioState !== 'stopped'"
        class="acl-rec-indicator"
        :class="{
          'is-paused':
            recordingAudioState === 'recording-paused' ||
            recordingAudioState === 'paused',
        }"
      >
        <span class="acl-rec-dot" />
        <span class="acl-rec-text">
          {{
            recordingAudioState === 'recording-paused' ||
            recordingAudioState === 'paused'
              ? 'Pausado'
              : 'Gravando'
          }}
        </span>
      </span>

      <!-- Aceleres: durante uma gravação ativa, os controles inline ficam
           visíveis (play/pause, finalizar, descartar). Sem isso o usuário
           não conseguiria interagir com o áudio sendo gravado. -->
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="showAudioPlayStopButton && isRecordingAudio"
        v-tooltip.top-end="composerTooltip('Finalizar e anexar áudio')"
        icon="i-ph-check"
        teal
        faded
        sm
        @click="finishAudioRecording"
      />
      <NextButton
        v-if="showAudioPlayStopButton && isRecordingAudio"
        v-tooltip.top-end="composerTooltip('Apagar gravação')"
        icon="i-ph-trash"
        ruby
        faded
        sm
        @click="deleteAudioRecording"
      />

      <!-- E-mail/canais que precisam: signature e quoted reply ficam fora
           do menu pra continuarem 1-clique no fluxo de e-mail. -->
      <NextButton
        v-if="showMessageSignatureButton && isAnEmailChannel"
        v-tooltip.top-end="composerTooltip(signatureToggleTooltip)"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="showQuotedReplyToggle"
        v-tooltip.top-end="composerTooltip(quotedReplyToggleTooltip)"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />

      <VideoCallButton
        v-if="
          (isAWebWidgetInbox || isAPIInbox) &&
          !isOnPrivateNote &&
          !isEditorDisabled
        "
        :conversation-id="conversationId"
      />
      <NextButton
        v-if="enableInsertArticleInReply"
        v-tooltip.top-end="
          composerTooltip(
            $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          )
        "
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
    </div>
    <div class="right-wrap">
      <template v-if="showSendButton">
        <NextButton
          v-if="isNote"
          :label="sendButtonText"
          type="submit"
          sm
          color="amber"
          :disabled="isSendDisabled"
          class="flex-shrink-0"
          @click="onSend"
        />
        <button
          v-else
          v-tooltip.top-end="composerTooltip(sendButtonText)"
          type="submit"
          class="wa-action-btn wa-action-btn--send"
          :disabled="isSendDisabled"
          :aria-label="sendButtonText"
          @click="onSend"
        >
          <i class="i-ph-paper-plane-right-fill" />
        </button>
      </template>
      <template v-else-if="showMicAsSend">
        <NextButton
          v-if="isNote"
          v-tooltip.top-end="
            composerTooltip(
              $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
            )
          "
          icon="i-ph-microphone"
          sm
          color="amber"
          class="flex-shrink-0"
          @click="toggleAudioRecorder"
        />
        <button
          v-else
          v-tooltip.top-end="
            composerTooltip(
              $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
            )
          "
          type="button"
          class="wa-action-btn wa-action-btn--mic"
          :aria-label="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
          @click="toggleAudioRecorder"
        >
          <i class="i-ph-microphone-fill" />
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply flex flex-1 items-center gap-2 min-w-0;
}

.right-wrap {
  @apply flex flex-shrink-0 items-center self-center pl-2 pb-0;
  min-width: 2.5rem;
  max-width: max-content;
  gap: 0.5rem;
}

.reply-bottom-panel {
  min-height: 3.5rem;
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}

.wa-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  flex: 0 0 auto;
  border-radius: 9999px;
  background: #00a884;
  color: #ffffff;
  border: 0;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.12);
  margin: 0;

  i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    line-height: 1;
    width: 1.25rem;
    height: 1.25rem;
  }

  &:hover:not(:disabled) {
    background: #008f6f;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Aceleres: indicador "Gravando" estilo WhatsApp — pílula com ponto
   vermelho pulsante + rótulo. is-paused deixa o ponto cinza e sem pulse. */
.acl-rec-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem 0.6rem 0.2rem 0.5rem;
  border-radius: 9999px;
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  user-select: none;
  flex-shrink: 0;

  &.is-paused {
    background: rgba(100, 116, 139, 0.12);
    color: #475569;
  }

  .acl-rec-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 9999px;
    background: #ef4444;
    flex-shrink: 0;
    animation: acl-rec-pulse 1.2s ease-in-out infinite;
  }

  &.is-paused .acl-rec-dot {
    background: #94a3b8;
    animation: none;
  }
}

@keyframes acl-rec-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.8);
  }
}

/* Aceleres: menu "+" — substitui a régua antiga de ícones inline (visual
   tipo WhatsApp Web). O wrap é relative pro popup ser posicionado
   absoluto encostado acima do botão. */
.acl-plus-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.acl-copilot-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.acl-copilot-button {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  padding: 0;
  background: transparent;
  cursor: pointer;
  transition:
    background 0.12s ease,
    transform 0.1s ease;

  i {
    width: 1.55rem;
    height: 1.55rem;
    background: linear-gradient(135deg, #0ea5e9 0%, #8b5cf6 45%, #f59e0b 100%);
  }

  &:hover,
  &.is-active {
    background: rgba(15, 23, 42, 0.06);
  }

  &:active {
    transform: scale(0.96);
  }
}

.acl-copilot-menu {
  bottom: calc(100% + 0.5rem);
  left: 0;
}

/* Esconde os <FileUpload> que servem só como gatilho de input file — o
   menu chama `document.getElementById('id').click()` neles. Mantém o
   componente montado pra o input estar no DOM. */
.acl-hidden-uploader {
  display: none !important;
}

.acl-plus-menu {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  z-index: 50;
  min-width: 17rem;
  padding: 0.5rem;
  border-radius: 0.875rem;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 12px 32px rgba(15, 23, 42, 0.14),
    0 3px 8px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Aceleres: itens maiores, texto 15px, ícone dentro de círculo colorido
   (estilo WhatsApp attach menu). (pedido 2026-07-01) */
.acl-plus-menu__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 0;
  background: transparent;
  border-radius: 0.625rem;
  font-size: 15px;
  font-weight: 500;
  color: #111827;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.06);
  }

  &--active {
    background: rgba(245, 158, 11, 0.10);
  }
}

.acl-plus-menu__icon {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;

  i {
    font-size: 1.375rem;
    line-height: 1;
  }
}

.acl-plus-menu__label {
  line-height: 1.2;
}

/* Cores WhatsApp-style — cada ação com sua paleta (ícone colorido, sem fundo). */
.acl-plus-menu__item--violet .acl-plus-menu__icon { color: #7c3aed; }
.acl-plus-menu__item--blue   .acl-plus-menu__icon { color: #2563eb; }
.acl-plus-menu__item--teal   .acl-plus-menu__icon { color: #0d9488; }
.acl-plus-menu__item--amber  .acl-plus-menu__icon { color: #d97706; }
.acl-plus-menu__item--slate  .acl-plus-menu__icon { color: #475569; }
.acl-plus-menu__item--aurora .acl-plus-menu__icon {
  color: #7c3aed;
}

.acl-plus-menu-fade-enter-active,
.acl-plus-menu-fade-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.acl-plus-menu-fade-enter-from,
.acl-plus-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.98);
}
</style>
